import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Observable, of, Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { DefaultHabitService } from './core/services/default-habit.service';
import { Habit } from './habit.model';
import { HabitService } from './habit.service';
import { ConfigService } from './shared/config/config.service';
import { HabitValidators } from './shared/habit/habit.validators';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  @ViewChild('modal', { read: ViewContainerRef, static: true })
  modal: ViewContainerRef | null = null;

  deleteDefaultHabitSub: Subscription | null = null;
  addDefaultHabitSub: Subscription | null = null;
  habits$: Observable<{ [key: string]: Habit }>;
  habits: { [key: string]: Habit };
  today: string;
  form: FormGroup;

  private defaultHabits: { [key: string]: Habit } = {};

  constructor(
    private habitService: HabitService,
    private defaultHabitService: DefaultHabitService,
    private fb: FormBuilder,
    private configService: ConfigService
  ) {
    this.today = this.calcToday();
    this.habitService.setupToday(this.today);
    this.habits$ = of({});
    this.habits = {};
    this.form = this.fb.group({
      habit: [
        '',
        [Validators.required],
        [
          HabitValidators.habitExistValidator(
            this.habitService.getCurrentHabitsDay(this.today)
          ),
        ],
      ],
    });
  }

  ngOnInit(): void {
    this.habits$ = this.defaultHabitService.getDefaultHabits().pipe(
      tap((habits) => (this.defaultHabits = habits)),
      switchMap(() =>
        this.habitService.watchCurrentHabitsDay(this.today).pipe(
          switchMap((habits) =>
            habits && Object.values(habits).length > 0
              ? of(habits)
              : this.saveDefaultHabits(this.defaultHabits)
          ),
          tap((habits: { [key: string]: Habit }) => {
            this.habits = habits;
          })
        )
      )
    );

    this.configService.setDom(this.modal);
  }

  addStrikeToHabit(habit: Habit): void {
    let strikes = this.habits[habit.id].strikes;

    if (!strikes) {
      strikes = 0;
    }

    this.habits[habit.id].strikes = strikes + 1;

    this.habitService.updateHabit(this.habits[habit.id], this.today).subscribe({
      next: (res) => null,
      error: (err) => console.log(err),
    });
  }

  removeStrikeToHabit(habit: Habit): void {
    const strikes = this.habits[habit.id].strikes;

    if (!strikes || strikes < 1) {
      this.habits[habit.id].strikes = 0;
    } else {
      this.habits[habit.id].strikes = strikes - 1;
    }

    this.habitService.saveHabit(this.habits, this.today).subscribe({
      next: (res) => null,
      error: (err) => console.log(err),
    });
  }

  deleteHabit(habit: Habit): void {
    delete this.habits[habit.id];

    this.habitService.saveHabit(this.habits, this.today).subscribe({
      next: (res) => null,
      error: (err) => console.log(err),
    });
  }

  calcToday(): string {
    const time = new Date();
    const day = time.getDate();
    const month = time.getMonth() + 1;
    const year = time.getFullYear();

    return `${day}-${month}-${year}`;
  }

  addHabit(): void {
    if (!this.habits) {
      this.habits = {};
    }

    if (!this.form.valid) {
      this.form.controls.habit.markAsTouched();
      return;
    }

    this.habits[this.form.value.habit] = {
      id: this.form.value.habit,
      name: this.form.value.habit,
      date: new Date().getTime(),
      strikes: 0,
    };

    this.habitService.saveHabit(this.habits, this.today).subscribe(
      () => this.form.reset(),
      (error) => console.log({ error })
    );
  }

  closeErrorBar(): void {
    this.form.reset();
  }

  trackByFn = (index: number, obj: any) => {
    return obj.key;
  }

  openConfig(): void {
    this.configService.openConfig(of(this.defaultHabits));
    this.watchCloseConfig();
    if (!this.deleteDefaultHabitSub) {
      this.watchDeleteDefaultHabit();
    }

    if (!this.addDefaultHabitSub) {
      this.watchAddDefaultHabit();
    }
  }

  watchCloseConfig(): void {
    this.configService
      .onClose()
      .pipe(take(1))
      .subscribe(() => {
        this.configService.closeConfig();
        if (this.deleteDefaultHabitSub) {
          this.deleteDefaultHabitSub.unsubscribe();
        }

        if (this.addDefaultHabitSub) {
          this.addDefaultHabitSub.unsubscribe();
        }
      });
  }

  watchDeleteDefaultHabit(): void {
    this.deleteDefaultHabitSub = this.configService
      .onDeleteDefaultHabit()
      .pipe(
        filter((habit) => !!habit),
        switchMap((habit) => {
          delete this.defaultHabits[(habit as Habit).id];
          return this.defaultHabitService.saveDefaultHabit(this.defaultHabits);
        })
      )
      .subscribe();
  }

  watchAddDefaultHabit(): void {
    this.addDefaultHabitSub = this.configService
      .onAddDefaultHabit()
      .subscribe((habit) => {
        if (!this.defaultHabits) {
          this.defaultHabits = {};
        }

        if (!habit) {
          return;
        }

        this.defaultHabits[habit] = {
          id: habit,
          name: habit,
          date: new Date().getTime(),
          strikes: 0,
        };

        this.defaultHabitService.saveDefaultHabit(this.defaultHabits).subscribe(
          () => this.form.reset(),
          (error) => console.log({ error })
        );
      });
  }

  private saveDefaultHabits(defaultHabits: {
    [key: string]: Habit;
  }): Observable<{ [key: string]: Habit }> {
    return this.habitService
      .saveHabit(defaultHabits, this.today)
      .pipe(map(() => defaultHabits));
  }
}
